import styled from 'styled-components';

export const DialogueBox = styled.div`
  border-radius: 5px;
  width: 100%;
  /* Fill the card and stack as a column so the action buttons can pin to
     the very bottom (via margin-top: auto). Content stays top-aligned. */
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

export const Header = styled.div`
  width: 100%;
  padding: 20px 24px;
  border-bottom: 1px solid #d6d6d6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f7eeff;
  border-radius: 10px 10px 0 0;
`;

export const DropdownContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  padding: 0 24px;
`;

export const DropdownItem = styled.div`
  margin-bottom: 16px;
`;

export const Title = styled.h3`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 18px;
  color: var(--text);
  margin-bottom: 10px;
`;

export const DropdownBox = styled.div`
  width: 100%;
`;

export const Button = styled.button`
  display: block;
  border-radius: 30px;
  /* margin-top: auto pins the button to the bottom of the flex-column card;
     the horizontal autos keep it centred. */
  margin: auto auto 16px;
  flex-shrink: 0;
  background-color: var(--light-mode-purple);
  font-family: var(--font-family-ibm_plex_sans);
  border: none;
  color: var(--white);
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  width: calc(100% - 48px);
  height: 40px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: 0.2s ease-in;

  &:hover {
    background-color: var(--royal-purple);
  }
`;

export const UsageGoalsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  /* Matches step 1's DropdownContainer rhythm so the question isn't crammed
     up against the "Step 2 of 2" label. */
  margin-top: 20px;
  margin-bottom: 8px;
`;

export const UsageGoalsSubtitle = styled.p`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  color: var(--text);
  opacity: 0.7;
  margin: -4px 0 16px;
`;

export const GoalOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 16px;
  line-height: 20px;
  color: var(--text);
  cursor: pointer;
`;

export const GoalCheckbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 2px 0 0 0 !important;
  accent-color: var(--light-mode-purple);
  cursor: pointer;
  flex-shrink: 0;
`;

export const OtherGoalInput = styled.input`
  margin: 12px 0 4px;
  padding: 10px 12px;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d6d6d6;
  border-radius: 8px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 15px;
  color: var(--text);

  &:focus {
    outline: none;
    border-color: var(--light-mode-purple);
  }
`;

// Wrapping "pill" selector for the usage-goals survey — lighter than a stacked
// checkbox list and roughly halves the vertical footprint.
export const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 10px;
`;

export const Chip = styled.button`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 15px;
  line-height: 18px;
  padding: 11px 18px;
  border-radius: 999px;
  border: 1.5px solid
    ${(props) => (props.selected ? 'var(--light-mode-purple)' : '#d6d6d6')};
  background-color: ${(props) =>
    props.selected ? 'var(--light-mode-purple)' : 'var(--white)'};
  color: ${(props) => (props.selected ? 'var(--white)' : 'var(--text)')};
  cursor: pointer;
  transition: 0.15s ease-in;

  &:hover {
    border-color: var(--light-mode-purple);
  }
`;

// Small "Step N of M" orienting label — short flows feel less daunting when
// the user can see there are only a couple of quick screens.
export const StepIndicator = styled.div`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--light-mode-purple);
  padding: 16px 24px 0;
`;

// Row holding [Back] + [Continue/Submit]. The primary (last) button flexes to
// fill; Back stays compact.
export const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 24px;
  /* margin-top: auto pins the Back + Submit row to the bottom of the card. */
  margin: auto 0 16px;
  flex-shrink: 0;

  button {
    margin: 0;
  }
  & > *:last-child {
    flex: 1;
    width: auto;
  }
`;

export const BackButton = styled.button`
  border-radius: 30px;
  background-color: var(--white);
  font-family: var(--font-family-ibm_plex_sans);
  border: 1.5px solid var(--light-mode-purple);
  color: var(--light-mode-purple);
  font-weight: 500;
  font-size: 16px;
  height: 40px;
  padding: 0 24px;
  cursor: pointer;
  transition: 0.2s ease-in;

  &:hover {
    background-color: #f7eeff;
  }
`;

export const TermsCondition = styled.div`
  padding: 10px 24px;
`;

export const WarningContainer = styled.div`
  margin-bottom: 16px;
  h3 {
    font-family: var(--font-family-ibm_plex_sans);
    font-weight: 500;
    font-size: var(--font-size-xl);
    line-height: 26px;
    color: var(--light-mode-purple);
    margin-bottom: 10px;
  }

  p {
    color: var(--text);
    font-family: var(--font-family-ibm_plex_sans);
    font-weight: 400;
    font-size: 18px;
    line-height: 23px;

    span {
      font-weight: 500;
    }
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const Checkbox = styled.input`
  width: 15px;
  height: 15px;
  margin-left: 0px !important;
`;

export const TermsText = styled.p`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 18px;
  font-weight: 400;
  line-height: 23px;
  color: var(--text);

  span {
    color: var(--light-mode-purple);
    cursor: pointer;
  }
`;

export const HeaderText = styled.div`
  font-family: var(--font-family-ibm_plex_sans);
  font-weight: 600;
  font-size: var(--font-size-xl);
`;

export const CloseImg = styled.img`
  width: 25px;
  height: 25px;
  cursor: pointer;
`;
